/**
 * PTEL Andalucía - Normalizador de Coordenadas v3.0
 * 
 * TD-002.E: Archivo de compatibilidad que re-exporta desde coordinate/
 * 
 * Este archivo se mantiene para:
 * 1. Compatibilidad con imports existentes
 * 2. Tipos ingleses para UI (NormalizationResult, ConfidenceLevel, etc.)
 * 3. Funciones async de validación (procesarYValidarCoordenadas)
 * 
 * Para nuevos desarrollos, importar directamente desde '@/lib/coordinate'
 * 
 * @version 3.0.0
 * @date 2026-01-26
 */

// ============================================================================
// RE-EXPORTS DESDE MÓDULO MODULARIZADO (coordinate/)
// ============================================================================

// Tipos base
export type {
  TipoCoordenda,
  NivelConfianza,
  PatronDetectado,
  ResultadoNormalizacion,
  ResultadoValidacion,
  ResultadoConcatenacion,
  ResultadoFormatoEspacial,
  ResultadoDMS,
  ResultadoNMEA,
  ResultadoParNMEA,
  ResultadoParEspaciosMultiples,
  ResultadoPrefiltroCodigo,
  ParCoordenadas,
} from './coordinate';

// Constantes
export { 
  RANGOS_ANDALUCIA,
  PLACEHOLDERS_TEXTO,
  PLACEHOLDERS_NUMERICOS,
  DISTANCIA_WARNING_METROS,
  DISTANCIA_CRITICA_METROS,
  EPSG_UTM30_ETRS89,
  EPSG_WGS84,
} from './coordinate';

// Detectores
export { 
  esPlaceholder, 
  esCodigo, 
  detectarParEspaciosMultiples,
  detectarPatron 
} from './coordinate';

// Separadores
export { 
  separarCoordenadasConcatenadas, 
  separarCoordenadasPegadas,
  type ResultadoCoordenadasPegadas 
} from './coordinate';

// Parsers
export { 
  parseDMS, 
  esDMS,
  parseNMEA, 
  parseNMEASentence, 
  parseParNMEA,
  esNMEA,
  parseWKT, 
  parseGeoJSON, 
  detectarFormatoEspacial 
} from './coordinate';

// Validadores
export { 
  validarCoordenada, 
  detectarIntercambioXY,
  type ResultadoIntercambioXY 
} from './coordinate';

// Normalizador principal
export { 
  normalizarCoordenada, 
  procesarParCoordenadas,
  transformarED50aETRS89,
  type OpcionesProcesarPar 
} from './coordinate';

// ============================================================================
// IMPORTS PARA FUNCIONES LOCALES
// ============================================================================

import type { 
  NivelConfianza, 
  PatronDetectado,
  ResultadoNormalizacion,
  ResultadoValidacion,
  ParCoordenadas 
} from './coordinate';

import { procesarParCoordenadas } from './coordinate';

import type {
  ResultadoValidacionUnificado,
  EntradaValidacionUnificada,
  OpcionesValidacionUnificada,
  NivelConfianzaUnificado
} from './unifiedValidation';
import { validarCoordenadaUnificada } from './unifiedValidation';

// ============================================================================
// TIPOS INGLESES PARA UI (COMPATIBILIDAD)
// ============================================================================

/** Nivel de confianza en inglés para componentes UI */
export type ConfidenceLevel = 'HIGH' | 'MEDIUM' | 'LOW' | 'CRITICAL';

/** Tipo de corrección aplicada */
export type CorrectionType = 
  | 'Y_TRUNCATED'
  | 'XY_SWAPPED'
  | 'PLACEHOLDER_DETECTED'
  | 'ENCODING_FIXED'
  | 'SEPARATOR_FIXED'
  | 'THOUSANDS_REMOVED'
  | 'DECIMAL_FIXED'
  | 'WHITESPACE_CLEANED';

/** Corrección individual aplicada durante normalización */
export interface Correction {
  type: CorrectionType;
  original: string;
  corrected: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  field: 'x' | 'y' | 'both';
  from: string;
  to: string;
}

/** Resultado de normalización en formato UI (inglés) */
export interface NormalizationResult {
  original: string;
  normalized: number | null;
  isValid: boolean;
  confidence: ConfidenceLevel;
  score: number;
  pattern: PatronDetectado;
  corrections: Correction[];
  warnings: string[];
  errors: string[];
  x?: number | null;
  y?: number | null;
  xOriginal?: string;
  yOriginal?: string;
  yCorrectionApplied?: boolean;
  yCorrectionType?: 'INSERT_4' | 'MULTIPLY_10' | 'EXTRA_DIGIT_REMOVAL' | 'WGS84_TO_UTM30' | 'WGS84_INV_TO_UTM30' | 'ED50_TO_ETRS89' | 'UTM30_MILES_EUROPEOS' | 'UTM30_CENTROID_CONFIRMED' | 'WGS84_CENTROID_CONVERTED' | null;
  yOriginalValue?: number;
}

/** Resultado de normalización para un PAR de coordenadas (X, Y) */
export interface NormalizationPairResult {
  x: number | null;
  y: number | null;
  xOriginal: string;
  yOriginal: string;
  isValid: boolean;
  confidence: ConfidenceLevel;
  score: number;
  corrections: Correction[];
  warnings: string[];
  errors: string[];
  swapped: boolean;
  epsg: number;
}

// ============================================================================
// FUNCIONES DE CONVERSIÓN (ESPAÑOL → INGLÉS)
// ============================================================================

/** Convierte NivelConfianza español a ConfidenceLevel inglés */
export function toConfidenceLevel(nivel: NivelConfianza): ConfidenceLevel {
  const map: Record<NivelConfianza, ConfidenceLevel> = {
    'ALTA': 'HIGH',
    'MEDIA': 'MEDIUM',
    'BAJA': 'LOW',
    'CRITICA': 'CRITICAL'
  };
  return map[nivel];
}


/** Convierte ResultadoNormalizacion a NormalizationResult */
export function toNormalizationResult(
  resultado: ResultadoNormalizacion,
  validacion?: ResultadoValidacion,
  field: 'x' | 'y' | 'both' = 'both'
): NormalizationResult {
  const corrections: Correction[] = [];
  
  resultado.fasesAplicadas.forEach(fase => {
    const baseCorrection = {
      original: resultado.valorOriginal,
      corrected: String(resultado.valorNormalizado ?? ''),
      from: resultado.valorOriginal,
      to: String(resultado.valorNormalizado ?? ''),
      field,
      description: fase
    };

    if (fase.includes('Y truncada')) {
      corrections.push({ ...baseCorrection, type: 'Y_TRUNCATED', priority: 'high' as const, field: 'y' });
    }
    if (fase.includes('Intercambio') || fase.includes('X↔Y')) {
      corrections.push({ ...baseCorrection, type: 'XY_SWAPPED', priority: 'high' as const, field: 'both' });
    }
    if (fase.includes('UTF-8') || fase.includes('Mojibake') || fase.includes('encoding')) {
      corrections.push({ ...baseCorrection, type: 'ENCODING_FIXED', priority: 'medium' as const });
    }
    if (fase.includes('separador') || fase.includes('decimal')) {
      corrections.push({ ...baseCorrection, type: 'SEPARATOR_FIXED', priority: 'low' as const });
    }
  });
  
  const confianza = validacion?.confianza || 'MEDIA';
  const scoreMap: Record<NivelConfianza, number> = { 'ALTA': 95, 'MEDIA': 75, 'BAJA': 50, 'CRITICA': 25 };
  
  return {
    original: resultado.valorOriginal,
    normalized: resultado.valorNormalizado,
    isValid: resultado.exito,
    confidence: toConfidenceLevel(confianza),
    score: scoreMap[confianza],
    pattern: resultado.patronDetectado,
    corrections,
    warnings: resultado.warnings,
    errors: resultado.errores,
    ...(field !== 'x' && resultado.correccionAplicada ? {
      yCorrectionApplied: resultado.correccionAplicada,
      yCorrectionType: resultado.tipoCorreccion,
      yOriginalValue: parseFloat(resultado.valorOriginal) || undefined
    } : {})
  };
}

/** Extrae campos de corrección Y para UI desde ParCoordenadas */
export function extractYCorrectionFields(par: ParCoordenadas): {
  yCorrectionApplied?: boolean;
  yCorrectionType?: 'INSERT_4' | 'MULTIPLY_10' | 'EXTRA_DIGIT_REMOVAL' | 'WGS84_TO_UTM30' | 'WGS84_INV_TO_UTM30' | 'ED50_TO_ETRS89' | 'UTM30_MILES_EUROPEOS' | 'UTM30_CENTROID_CONFIRMED' | 'WGS84_CENTROID_CONVERTED' | null;
  yOriginalValue?: number;
} {
  const normY = par.normalizacionY;
  if (!normY.correccionAplicada) return {};
  return {
    yCorrectionApplied: normY.correccionAplicada,
    yCorrectionType: normY.tipoCorreccion,
    yOriginalValue: parseFloat(normY.valorOriginal) || undefined
  };
}

/** Convierte ParCoordenadas a NormalizationPairResult */
export function toNormalizationPairResult(par: ParCoordenadas): NormalizationPairResult {
  const corrections: Correction[] = [];
  const allWarnings: string[] = [];
  const allErrors: string[] = [];
  
  [par.normalizacionX, par.normalizacionY].forEach((norm, idx) => {
    const axis = idx === 0 ? 'x' : 'y';
    const axisLabel = idx === 0 ? 'X' : 'Y';
    
    allWarnings.push(...norm.warnings.map(w => `${axisLabel}: ${w}`));
    allErrors.push(...norm.errores.map(e => `${axisLabel}: ${e}`));
    
    norm.fasesAplicadas.forEach(fase => {
      const baseCorrection = {
        original: norm.valorOriginal,
        corrected: String(norm.valorNormalizado ?? ''),
        from: norm.valorOriginal,
        to: String(norm.valorNormalizado ?? ''),
        field: axis as 'x' | 'y',
        description: `${axisLabel}: ${fase}`
      };

      if (fase.includes('Y truncada')) {
        corrections.push({ ...baseCorrection, type: 'Y_TRUNCATED', priority: 'high' as const });
      } else if (fase.includes('Intercambio') || fase.includes('X↔Y')) {
        corrections.push({ ...baseCorrection, type: 'XY_SWAPPED', priority: 'high' as const, field: 'both' });
      } else if (fase.includes('UTF-8') || fase.includes('Mojibake')) {
        corrections.push({ ...baseCorrection, type: 'ENCODING_FIXED', priority: 'medium' as const });
      } else if (fase.includes('separador') || fase.includes('decimal')) {
        corrections.push({ ...baseCorrection, type: 'SEPARATOR_FIXED', priority: 'low' as const });
      }
    });
  });
  
  const scoreMap: Record<NivelConfianza, number> = { 'ALTA': 95, 'MEDIA': 75, 'BAJA': 50, 'CRITICA': 25 };
  
  return {
    x: par.x,
    y: par.y,
    xOriginal: par.xOriginal,
    yOriginal: par.yOriginal,
    isValid: par.normalizacionX.exito && par.normalizacionY.exito,
    confidence: toConfidenceLevel(par.confianzaGlobal),
    score: scoreMap[par.confianzaGlobal],
    corrections,
    warnings: allWarnings,
    errors: allErrors,
    swapped: par.intercambioAplicado,
    epsg: par.epsg
  };
}


// ============================================================================
// VAL-08: PROCESAMIENTO + VALIDACIÓN UNIFICADA (ASYNC)
// ============================================================================

/** Contexto municipal y de infraestructura para procesamiento con validación */
export interface ContextoProcesamiento {
  municipio: string;
  codigoINE: string;
  nombre?: string;
  direccion?: string;
  tipo?: string;
}

/** Opciones extendidas para procesamiento con validación */
export interface OpcionesProcesamientoValidacion {
  aplicarCorreccionP0?: boolean;
  detectarIntercambio?: boolean;
  detectarConcatenacion?: boolean;
  epsgAsumido?: number;
  validarDistancia?: boolean;
  validarGeocoding?: boolean;
  radioMaxKm?: number;
  umbralDiscrepanciaMetros?: number;
}

/** Resultado combinado: normalización + validación unificada */
export interface ResultadoProcesamiento {
  normalizacion: ParCoordenadas;
  validacion: ResultadoValidacionUnificado | null;
  x: number | null;
  y: number | null;
  scoreTotal: number;
  nivelConfianza: NivelConfianzaUnificado | null;
  propuestaCorreccion: boolean;
  coordenadasAlternativas?: { x: number; y: number };
  timestampProcesamiento: string;
}

const OPCIONES_PROCESAMIENTO_DEFAULT: Required<OpcionesProcesamientoValidacion> = {
  aplicarCorreccionP0: true,
  detectarIntercambio: true,
  detectarConcatenacion: true,
  epsgAsumido: 25830,
  validarDistancia: true,
  validarGeocoding: true,
  radioMaxKm: 15,
  umbralDiscrepanciaMetros: 500,
};

/**
 * Procesa y valida coordenadas de forma unificada (ASYNC)
 * Combina normalización síncrona + validación asíncrona
 */
export async function procesarYValidarCoordenadas(
  xInput: string,
  yInput: string,
  contexto: ContextoProcesamiento,
  opciones: OpcionesProcesamientoValidacion = {}
): Promise<ResultadoProcesamiento> {
  const opts = { ...OPCIONES_PROCESAMIENTO_DEFAULT, ...opciones };
  const timestamp = new Date().toISOString();
  
  // 1. NORMALIZACIÓN SÍNCRONA
  const normalizacion = procesarParCoordenadas(xInput, yInput, {
    aplicarCorreccionP0: opts.aplicarCorreccionP0,
    detectarIntercambio: opts.detectarIntercambio,
    detectarConcatenacion: opts.detectarConcatenacion,
    epsgAsumido: opts.epsgAsumido,
    codigoINE: contexto.codigoINE,
  });
  
  // 2. VALIDACIÓN ASÍNCRONA (si hay coordenadas válidas)
  let validacion: ResultadoValidacionUnificado | null = null;
  
  if (normalizacion.x !== null && normalizacion.y !== null) {
    const entradaValidacion: EntradaValidacionUnificada = {
      coordenadas: { x: normalizacion.x, y: normalizacion.y },
      municipio: contexto.municipio,
      codigoINE: contexto.codigoINE,
      nombre: contexto.nombre,
      direccion: contexto.direccion,
      tipo: contexto.tipo,
    };
    
    const opcionesValidacion: OpcionesValidacionUnificada = {
      validarDistancia: opts.validarDistancia,
      validarGeocoding: opts.validarGeocoding,
      radioMaxKm: opts.radioMaxKm,
      umbralDiscrepanciaMetros: opts.umbralDiscrepanciaMetros,
    };
    
    validacion = await validarCoordenadaUnificada(entradaValidacion, opcionesValidacion);
  }
  
  // 3. CONSTRUIR RESULTADO COMBINADO
  const resultado: ResultadoProcesamiento = {
    normalizacion,
    validacion,
    x: normalizacion.x,
    y: normalizacion.y,
    scoreTotal: validacion?.scoreTotal ?? 0,
    nivelConfianza: validacion?.nivelConfianza ?? null,
    propuestaCorreccion: validacion?.propuestaCorreccion ?? false,
    timestampProcesamiento: timestamp,
  };
  
  if (validacion?.propuestaCorreccion && validacion.coordenadasAlternativas) {
    resultado.coordenadasAlternativas = validacion.coordenadasAlternativas;
  }
  
  return resultado;
}

// ============================================================================
// UTILIDADES
// ============================================================================

/** Formatea coordenada para visualización */
export function formatearCoordenada(valor: number | null, decimales: number = 2): string {
  if (valor === null) return '-';
  return valor.toFixed(decimales);
}

/** Genera diagnóstico legible de un par de coordenadas */
export function generarDiagnostico(par: ParCoordenadas): string {
  const lines: string[] = [];
  lines.push(`Entrada: X="${par.xOriginal}", Y="${par.yOriginal}"`);
  lines.push(`Resultado: X=${formatearCoordenada(par.x)}, Y=${formatearCoordenada(par.y)}`);
  lines.push(`EPSG: ${par.epsg}`);
  lines.push(`Confianza: ${par.confianzaGlobal}`);
  if (par.intercambioAplicado) lines.push('⚠️ Intercambio X↔Y aplicado');
  if (par.normalizacionY.correccionAplicada) lines.push('⚠️ Corrección Y truncada aplicada');
  return lines.join('\n');
}
